/**
 * Avatar helpers — client-side WebP transcoding + R2 URL composition.
 *
 * Avatars are bounded to a small max size (512px) and re-encoded to
 * WebP at quality 0.85 before upload. That gives ~20–60 KB files
 * regardless of what the user picked, keeps Cloudflare R2 storage
 * tiny, and means the <img> on a story card loads instantly. The
 * server-side allowlist also accepts JPEG/PNG for any code path that
 * skips this helper.
 */

const AVATAR_MAX_DIM = 512;
const AVATAR_WEBP_QUALITY = 0.85;

const R2_PUBLIC_BASE = (process.env.REACT_APP_R2_PUBLIC_BASE || '').replace(/\/$/, '');

/**
 * Transcode a user-picked image File to a WebP Blob, resized so that the
 * longer edge is at most AVATAR_MAX_DIM. Preserves aspect ratio. Throws
 * if the browser can't decode the file (corrupt, unsupported format).
 */
export async function transcodeAvatarToWebp(file) {
  if (!file) throw new Error('No file provided');

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('Could not read this image. Try a different file.');
  });

  const ratio = Math.min(
    AVATAR_MAX_DIM / bitmap.width,
    AVATAR_MAX_DIM / bitmap.height,
    1, // never upscale
  );
  const w = Math.max(1, Math.round(bitmap.width * ratio));
  const h = Math.max(1, Math.round(bitmap.height * ratio));

  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);

  // OffscreenCanvas → convertToBlob; HTMLCanvasElement → toBlob (callback).
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/webp', quality: AVATAR_WEBP_QUALITY });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('WebP encode failed'))),
      'image/webp',
      AVATAR_WEBP_QUALITY,
    );
  });
}

/**
 * Compose a public R2 URL for an avatar object key.
 *
 * Returns null if either piece is missing (frontend then falls back to
 * initials / placeholder). Optional `cacheBust` should be the profile's
 * `updated_at` so the browser/CDN refetches after re-upload — avatars
 * overwrite at the same path, so without a cache-buster the old image
 * sticks around.
 *
 * `fallbackUrl` is rendered when the user hasn't uploaded an avatar
 * yet — typically the OAuth provider's avatar from
 * `auth.users.user_metadata.avatar_url`. It only helps the currently
 * signed-in user's own view; for cross-user attribution (story cards
 * etc.) the profile must have a real `avatar_object_key`, which is
 * what `importProviderAvatar()` backfills on first profile load.
 */
export function composeAvatarUrl(objectKey, cacheBust, fallbackUrl = null) {
  if (!objectKey || !R2_PUBLIC_BASE) return fallbackUrl || null;
  const url = `${R2_PUBLIC_BASE}/${objectKey}`;
  return cacheBust ? `${url}?v=${encodeURIComponent(cacheBust)}` : url;
}
